import React, { useState, useMemo } from "react";
import {
  Package,
  CheckCircle,
  MapPin,
  Truck,
  Play,
  ChevronUp,
  ChevronDown,
  Tag,
} from "lucide-react";
import "./index.css";
import {
  CartLoading,
  CartLoadingAllocation,
  CartLoadingDetailResponse,
  CartStatus,
  Gate,
  GroupOrderDetails,
  Order,
} from "../../../../redux/cartLoadingSlice";
import DataTable, { TableColumn } from "react-data-table-component";
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";

interface LoadingManagementProps {
  cart: CartLoading;
  cartAllocations: CartLoadingAllocation[];
  gates: Gate[];
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  getTimeRemaining: (targetTime: string | null) => string;
  updateCartState: (
    cartId: string,
    status: string,
    gateId?: string,
    groupedOrderIds?: string[],
  ) => void;
  setSupervisorTab: (tab: string) => void;
  setSelectedCart: (cartId: string) => void;
  cartDetailData?: CartLoadingDetailResponse;
  isCartDetailLoading: boolean;
  updatingCarts: Record<string, string>;
}

// Interface for grouped items data
interface GroupedItem {
  menuItemId: string;
  displayName: string;
  totalQuantity: number;
  totalValue: number;
  portionSize?: string;
  groups: Map<string, any>;
  orders: any[];
}

const LoadingManagement: React.FC<LoadingManagementProps> = ({
  cart,
  gates,
  getStatusColor,
  getStatusText,
  getTimeRemaining,
  updateCartState,
  setSupervisorTab,
  cartDetailData,
  updatingCarts,
  isCartDetailLoading,
}) => {
  const [loadingViewMode, setLoadingViewMode] = useState<"orders" | "items">(
    "orders",
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Create filtered versions of the data using useMemo instead of mutating props
  const filteredCartDetailData = useMemo(() => {
    if (!cartDetailData) return null;

    // Create a deep copy of the data to avoid mutating props
    const filteredData = JSON.parse(JSON.stringify(cartDetailData));

    // Filter individual orders
    if (filteredData.ordersForCart) {
      filteredData.ordersForCart = filteredData.ordersForCart.filter(
        (order: any) => order.orderStatus !== "Issued by Kitchen"
      );
    }

    // Filter group orders
    if (filteredData.groupOrderDetailsList) {
      filteredData.groupOrderDetailsList = filteredData.groupOrderDetailsList
        .map((group: any) => ({
          ...group,
          orders: group.orders.filter((order: any) => order.orderStatus !== "Issued by Kitchen")
        }))
        .filter((group: any) => group.orders.length > 0); // Remove groups with no orders
    }

    return filteredData;
  }, [cartDetailData]);

  const isButtonLoading = (cartId: string, status: string) => {
    return updatingCarts[`${cartId}-${status}`] === "processing";
  };

  // Helper function to get grouped order IDs from cart
  const getGroupedOrderIdsFromCart = (cart: CartLoading): string[] => {
    if (
      !cart.groupOrders ||
      !Array.isArray(cart.groupOrders) ||
      cart.groupOrders.length === 0
    ) {
      return [];
    }

    return cart.groupOrders
      .filter((group) => group && group.id)
      .map((group) => group.id);
  };

  const cartGate = gates.find((g) => g.id === cart.gateId);

  const stateHistory = filteredCartDetailData?.currentLoadingDoc?.stateHistory || {};

  const formatFirestoreTime = (
    timestamp: { _seconds: number; _nanoseconds: number } | null,
  ): string => {
    if (!timestamp) return "-";
    return new Date(timestamp._seconds * 1000).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getChangedByDisplayName = (changedBy: any): string => {
    if (!changedBy) return "Unknown";
    return changedBy.displayName || "Unknown User";
  };

  // Get group order details from filtered data
  const groupOrderDetails: GroupOrderDetails[] =
    filteredCartDetailData?.groupOrderDetailsList || [];

  // Get all orders (both individual and grouped) from filtered data
  const allOrders: Order[] = [
    ...(filteredCartDetailData?.ordersForCart || []),
    ...groupOrderDetails.flatMap((group) => group.orders),
  ];

  // Generate colors for groups
  const groupColors = [
    "blue",
    "green",
    "purple",
    "orange",
    "pink",
    "teal",
    "indigo",
    "amber",
  ];

  const getGroupColor = (groupId: string, index: number) => {
    const colorIndex = index % groupColors.length;
    return groupColors[colorIndex];
  };

  const getGroupColorClasses = (color: string) => {
    const colorClasses = {
      blue: "bg-blue-50 border-blue-200 text-blue-800",
      green: "bg-green-50 border-green-200 text-green-800",
      purple: "bg-purple-50 border-purple-200 text-purple-800",
      orange: "bg-orange-50 border-orange-200 text-orange-800",
      pink: "bg-pink-50 border-pink-200 text-pink-800",
      teal: "bg-teal-50 border-teal-200 text-teal-800",
      indigo: "bg-indigo-50 border-indigo-200 text-indigo-800",
      amber: "bg-amber-50 border-amber-200 text-amber-800",
    };
    return (
      colorClasses[color as keyof typeof colorClasses] || colorClasses.blue
    );
  };

  const getGroupBadgeColor = (color: string) => {
    const colorClasses = {
      blue: "bg-blue-100 text-blue-800 border-blue-300",
      green: "bg-green-100 text-green-800 border-green-300",
      purple: "bg-purple-100 text-purple-800 border-purple-300",
      orange: "bg-orange-100 text-orange-800 border-orange-300",
      pink: "bg-pink-100 text-pink-800 border-pink-300",
      teal: "bg-teal-100 text-teal-800 border-teal-300",
      indigo: "bg-indigo-100 text-indigo-800 border-indigo-300",
      amber: "bg-amber-100 text-amber-800 border-amber-300",
    };
    return (
      colorClasses[color as keyof typeof colorClasses] || colorClasses.blue
    );
  };

  const toggleGroupExpanded = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Consolidated items view data using filtered data
  const getConsolidatedItemsData = () => {
    const groupedItems: GroupedItem[] = [];

    const individualItems: Array<{
      menuItemId: string;
      displayName: string;
      totalQuantity: number;
      totalValue: number;
      portionSize?: string;
      orders: any[];
    }> = [];

    // Process grouped orders
    groupOrderDetails.forEach((group, groupIndex) => {
      const groupColor = getGroupColor(group.groupOrderId, groupIndex);

      group.orders.forEach((order) => {
        order.orderItems.forEach((item) => {
          const key = item.menuItemId;

          // Find or create grouped item
          let groupedItem = groupedItems.find((i) => i.menuItemId === key);
          if (!groupedItem) {
            groupedItem = {
              menuItemId: item.menuItemId,
              displayName: item.displayName,
              totalQuantity: 0,
              totalValue: 0,
              portionSize: item.portionSize,
              groups: new Map(),
              orders: [],
            };
            groupedItems.push(groupedItem);
          }

          groupedItem.totalQuantity += item.quantity;
          groupedItem.totalValue += item.quantity * item.unitPrice;

          // Track by group
          if (!groupedItem.groups.has(group.groupOrderId)) {
            groupedItem.groups.set(group.groupOrderId, {
              groupId: group.groupOrderId,
              groupName: `Group ${groupIndex + 1}`,
              groupColor: groupColor,
              quantity: 0,
            });
          }
          const groupData = groupedItem.groups.get(group.groupOrderId)!;
          groupData.quantity += item.quantity;

          // Track by order
          groupedItem.orders.push({
            orderId: order.id,
            orderReference: order.orderReference,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            groupName: `Group ${groupIndex + 1}`,
          });
        });
      });
    });

    // Process individual orders (not in groups) - using filtered data
    filteredCartDetailData?.ordersForCart?.forEach((order: any) => {
      order.orderItems.forEach((item: any) => {
        const key = item.menuItemId;

        // Find or create individual item
        let individualItem = individualItems.find((i) => i.menuItemId === key);
        if (!individualItem) {
          individualItem = {
            menuItemId: item.menuItemId,
            displayName: item.displayName,
            totalQuantity: 0,
            totalValue: 0,
            portionSize: item.portionSize,
            orders: [],
          };
          individualItems.push(individualItem);
        }

        individualItem.totalQuantity += item.quantity;
        individualItem.totalValue += item.quantity * item.unitPrice;

        // Track by order
        individualItem.orders.push({
          orderId: order.id,
          orderReference: order.orderReference,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          groupName: null, // No group for individual orders
        });
      });
    });

    return {
      groupedItems,
      individualItems,
    };
  };

  const { groupedItems } = getConsolidatedItemsData();

  // DataTable columns for grouped items
  const groupedItemsColumns: TableColumn<GroupedItem>[] = [
    {
      name: <h6 className="fw-bold">Item Name</h6>,
      selector: (row) => row.displayName,
      sortable: true,
      cell: (row) => (
        <div>
          <div className="loading-management-item-name">{row.displayName}</div>
          {row.portionSize && (
            <div className="loading-management-item-portion">
              Portion: {row.portionSize}
            </div>
          )}
        </div>
      ),
      width: "25%",
    },
    {
      name: <h6 className="fw-bold">Groups</h6>,
      sortable: true,
      cell: (row) => (
        <div className="loading-management-item-groups">
          {Array.from(row.groups.values()).map((group: any) => (
            <div
              key={group.groupId}
              className={`loading-management-group-badge ${getGroupBadgeColor(
                group.groupColor,
              )}`}
            >
              <Tag size={12} className="loading-management-group-badge-icon" />
              {group.groupName}
              <span className="loading-management-group-quantity">
                ({group.quantity})
              </span>
            </div>
          ))}
        </div>
      ),
      width: "25%",
    },
    {
      name: <h6 className="fw-bold">Total Quantity</h6>,
      selector: (row) => row.totalQuantity,
      sortable: true,
      cell: (row) => (
        <div className="loading-management-item-total-quantity">
          {row.totalQuantity} items
        </div>
      ),
      center: true,
    },
    {
      name: <h6 className="fw-bold">Total Value</h6>,
      selector: (row) => row.totalValue,
      sortable: true,
      cell: (row) => (
        <div className="loading-management-item-total-value">
          LKR {row.totalValue.toFixed(2)}
        </div>
      ),
      center: true,
    },
  ];

  // Show loading spinner when cart detail data is being fetched
  if (isCartDetailLoading) {
    return (
      <div className="loading-management-container">
        <div className="loading-management-header">
          <div className="loading-management-header-left">
            <button
              onClick={() => setSupervisorTab("carts")}
              className="loading-management-back-button"
            >
              ← Back to Cart List
            </button>
          </div>
        </div>
        <div className="loading-management-loading">
          <Spinner animation="border" role="status" variant="primary" />
        </div>
      </div>
    );
  }

  // Check if cart has any orders after filtering
  const hasAnyOrders = allOrders.length > 0 || groupOrderDetails.length > 0;

  if (!hasAnyOrders) {
    return (
      <div className="loading-management-container">
        <div className="loading-management-header">
          <div className="loading-management-header-left">
            <button
              onClick={() => setSupervisorTab("carts")}
              className="loading-management-back-button"
            >
              ← Back to Cart List
            </button>
          </div>
        </div>
        <div className="loading-management-no-orders">
          <Package size={48} className="loading-management-no-orders-icon" />
          <h3>No Active Orders</h3>
          <p>This cart has no active orders. All orders have been marked as "Issued by Kitchen".</p>
          <button
            onClick={() => setSupervisorTab("carts")}
            className="loading-management-back-to-carts"
          >
            Return to Cart List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="loading-management-container">
      <div className="loading-management-header">
        <div className="loading-management-header-left">
          <button
            onClick={() => setSupervisorTab("carts")}
            className="loading-management-back-button"
          >
            ← Back to Cart List
          </button>
        </div>
        <div className="loading-management-header-right">
          {/* Empty for now, can add other header elements */}
        </div>
      </div>

      <div className="loading-management-summary">
        <div className="loading-management-summary-grid">
        
          <div className="loading-management-summary-item">
            <p className="loading-management-summary-label">Rider</p>
            <p className="loading-management-summary-value">
              {filteredCartDetailData?.cartPerson || cart.rider || "Unassigned"}
            </p>
          </div>
          <div className="loading-management-summary-item">
            <p className="loading-management-summary-label">Gate & Status</p>
            <div className="loading-management-status-container">
              <p className="loading-management-gate">
                {cartGate?.name || "Not Assigned"}
              </p>
              <span
                className={`loading-management-status ${getStatusColor(
                  filteredCartDetailData?.currentLoadingDoc?.activeState || cart.status,
                )}`}
              >
                {getStatusText(
                  filteredCartDetailData?.currentLoadingDoc?.activeState || cart.status,
                )}
              </span>
            </div>
          </div>

          <div className="loading-management-summary-item">
            <p className="loading-management-summary-label">Time Remaining</p>
            <p
              className={`loading-management-time-remaining ${
                getTimeRemaining(cart.estimatedLeaveTime) === "Overdue"
                  ? "loading-management-time-overdue"
                  : "loading-management-time-normal"
              }`}
            >
              {getTimeRemaining(cart.estimatedLeaveTime)}
            </p>
          </div>
        </div>
      </div>

      {/* Individual Orders Section - Only show if there are individual orders */}
      {filteredCartDetailData?.ordersForCart &&
        filteredCartDetailData.ordersForCart.length > 0 && (
          <div className="loading-management-orders-group">
            <div className="loading-management-individual-orders-header">
              <h4 className="loading-management-group-title">
                Individual Orders
              </h4>
              <button
                onClick={() => setSupervisorTab("order-selection")}
                className={`loading-management-group-orders-button ${
                  // Disable button for CartPersonVerification and beyond
                  cart.status === "CartPersonVerification" ||
                  cart.status === "GateAssigned" ||
                  cart.status === "AtGate" ||
                  cart.status === "CartCanLeave"
                    ? "loading-management-group-orders-button-disabled"
                    : ""
                }`}
                disabled={
                  cart.status === "CartPersonVerification" ||
                  cart.status === "GateAssigned" ||
                  cart.status === "AtGate" ||
                  cart.status === "CartCanLeave"
                }
              >
                <Tag className="loading-management-group-orders-icon" />
                Group Orders
              </button>
            </div>

            <div className="loading-management-orders-grid">
              {filteredCartDetailData.ordersForCart.map((order: any) => (
                <div key={order.id} className="loading-management-order-card">
                  <div className="loading-management-order-header">
                    <div className="loading-management-order-info">
                      <h5 className="loading-management-order-id">
                        {order.orderReference}
                      </h5>
                    </div>
                    <div className="loading-management-order-status">
                      <span
                        className={`loading-management-order-status-badge ${getStatusColor(
                          order.orderStatus,
                        )}`}
                      >
                        {order.orderStatus}
                      </span>
                    </div>
                  </div>
                  <div className="loading-management-order-items">
                    <div className="loading-management-order-items-list">
                      {order.orderItems.map((item: any, index: number) => (
                        <div
                          key={index}
                          className="loading-management-order-item"
                        >
                          <span className="loading-management-item-name">
                            {item.displayName}
                          </span>
                          <span className="loading-management-item-quantity">
                            ×{item.quantity}
                          </span>
                          <span className="loading-management-item-price">
                            LKR {item.unitPrice}
                          </span>
                          {item.portionSize && (
                            <span className="loading-management-item-portion">
                              ({item.portionSize})
                            </span>
                          )}
                          <div className="loading-management-order-total">
                            Total: LKR {order.totalPrice}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Orders by Group Section - Only show if there are group orders */}
      {groupOrderDetails.length > 0 && (
        <div className="loading-management-orders-section">
          <div className="loading-management-section-header">
            <h3 className="loading-management-section-title">
              Orders by Group
            </h3>
            <div className="loading-management-header-actions">
              <div className="loading-management-view-toggle">
                <button
                  onClick={() => setLoadingViewMode("orders")}
                  className={`loading-management-view-button ${
                    loadingViewMode === "orders"
                      ? "loading-management-view-button-active"
                      : ""
                  }`}
                >
                  Orders by Group
                </button>
                <button
                  onClick={() => setLoadingViewMode("items")}
                  className={`loading-management-view-button ${
                    loadingViewMode === "items"
                      ? "loading-management-view-button-active"
                      : ""
                  }`}
                >
                  Grouped Items
                </button>
              </div>
            </div>
          </div>

          {loadingViewMode === "orders" ? (
            <div className="loading-management-orders-content">
              {/* Grouped Orders */}
              {groupOrderDetails.map((group, groupIndex) => {
                const groupColor = getGroupColor(
                  group.groupOrderId,
                  groupIndex,
                );
                const isExpanded = expandedGroups.has(group.groupOrderId);

                return (
                  <div
                    key={group.groupOrderId}
                    className={`loading-management-orders-group ${getGroupColorClasses(
                      groupColor,
                    )}`}
                  >
                    <div
                      className="loading-management-group-header"
                      onClick={() => toggleGroupExpanded(group.groupOrderId)}
                    >
                      <div className="loading-management-group-title-container">
                        <Tag className="loading-management-group-icon" />
                        <h4 className="loading-management-group-title">
                          Group {groupIndex + 1}
                        </h4>
                        <span className="loading-management-group-count">
                          {group.orders.length} orders
                        </span>
                      </div>
                      <div className="loading-management-group-actions">
                        {isExpanded ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="loading-management-orders-grid">
                        {group.orders.map((order) => (
                          <div
                            key={order.id}
                            className="loading-management-order-card"
                          >
                            <div className="loading-management-order-header">
                              <div className="loading-management-order-info">
                                <h5 className="loading-management-order-id">
                                  {order.orderReference}
                                </h5>
                              </div>
                              <div className="loading-management-order-status">
                                <span
                                  className={`loading-management-order-status-badge ${getStatusColor(
                                    order.orderStatus,
                                  )}`}
                                >
                                  {order.orderStatus}
                                </span>
                              </div>
                            </div>
                            <div className="loading-management-order-items">
                              <h6 className="loading-management-order-items-title">
                                Items:
                              </h6>
                              <div className="loading-management-order-items-list">
                                {order.orderItems.map((item, index) => (
                                  <div
                                    key={index}
                                    className="loading-management-order-item"
                                  >
                                    <span className="loading-management-item-name">
                                      {item.displayName}
                                    </span>
                                    <span className="loading-management-item-quantity">
                                      ×{item.quantity}
                                    </span>
                                    <span className="loading-management-item-price">
                                      LKR {item.unitPrice}
                                    </span>
                                    {item.portionSize && (
                                      <span className="loading-management-item-portion">
                                        ({item.portionSize})
                                      </span>
                                    )}
                                    <div className="loading-management-order-total">
                                      Total: LKR {order.totalPrice}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="loading-management-items-content">
              {/* Grouped Items Section */}
              {groupedItems.length > 0 && (
                <div className="loading-management-items-section">
                  <h4 className="loading-management-items-section-title">
                    <Tag className="loading-management-section-title-icon" />
                    Grouped Items
                  </h4>

                  {/* DataTable for Grouped Items */}
                  <div className="loading-management-items-datatable">
                    <DataTable
                      columns={groupedItemsColumns}
                      data={groupedItems}
                      responsive
                      pagination
                      highlightOnHover
                      striped
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      noDataComponent={
                        <div className="loading-management-no-items">
                          <Package className="loading-management-no-items-icon" />
                          <p className="loading-management-no-items-text">
                            No grouped items to display
                          </p>
                        </div>
                      }
                    />
                  </div>
                </div>
              )}

              {/* No Items Message - Only show if there are no items at all */}
              {groupedItems.length === 0 && (
                <div className="loading-management-no-items">
                  <Package className="loading-management-no-items-icon" />
                  <p className="loading-management-no-items-text">
                    No grouped items to display
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Gate assignment section - Show in CartPersonVerification state */}
      {cart.status === "CartPersonVerification" && (
        <div className="loading-management-gate-selection">
          <h4 className="loading-management-gate-selection-title">
            🚪 Gate Assignment
          </h4>
          <div className="loading-management-gate-selection-content">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  updateCartState(cart.id, "GateAssigned", e.target.value);
                }
              }}
              className="cart-gate-select"
              value=""
            >
              <option value="">🚪 Select Gate...</option>
              {gates.map((gate) => (
                <option key={gate.id} value={gate.id}>
                  📍 {gate.name} {gate.id === cart.gateId ? "(Current)" : ""}
                </option>
              ))}
            </select>
            {cart.gateId && (
              <p className="cart-current-gate-info">
                Current gate: {gates.find((g) => g.id === cart.gateId)?.name}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Loading Management Timeline */}
      <div className="loading-management-timeline">
        <h3 className="loading-management-timeline-title">Workflow Timeline</h3>
        <div className="loading-management-timeline-steps">
          {[
            {
              key: "LoadingStarted",
              label: "Loading Started",
              icon: Package,
            },
            {
              key: "CratesCollected",
              label: "TL Collected Crates",
              icon: Package,
            },
            {
              key: "TLItemsVerification",
              label: "TL Item Verification",
              icon: CheckCircle,
            },
            {
              key: "CartPersonVerification",
              label: "Cart Person Verification",
              icon: CheckCircle,
            },
            {
              key: "GateAssigned",
              label: "Gate Assigned",
              icon: MapPin,
            },
            {
              key: "AtGate",
              label: "Cart At Gate",
              icon: Truck,
            },
            {
              key: "CartCanLeave",
              label: "Cart Can Leave",
              icon: CheckCircle,
            },
          ].map((step) => {
            const Icon = step.icon;
            const history = stateHistory[step.key as CartStatus];
            const isCompleted = history?.status === "Completed";
            const changedBy = history?.changedBy;
            const updatedAt = history?.updatedAt;

            return (
              <div key={step.key} className="loading-management-timeline-step">
                <div
                  className={`loading-management-timeline-icon ${
                    isCompleted
                      ? "loading-management-timeline-icon-completed"
                      : "loading-management-timeline-icon-pending"
                  }`}
                >
                  <Icon className="loading-management-timeline-step-icon" />
                </div>
                <div className="loading-management-timeline-content">
                  <div className="loading-management-timeline-header">
                    <span
                      className={`loading-management-timeline-label ${
                        isCompleted
                          ? "loading-management-timeline-label-completed"
                          : "loading-management-timeline-label-pending"
                      }`}
                    >
                      {step.label}
                    </span>
                    <span className="loading-management-timeline-actor">
                      (
                      {isCompleted && changedBy
                        ? getChangedByDisplayName(changedBy)
                        : "Upcoming"}
                      )
                    </span>
                    {isCompleted && updatedAt && (
                      <span className="loading-management-timeline-time">
                        {formatFirestoreTime(updatedAt)}
                      </span>
                    )}
                    {isCompleted && (
                      <span className="loading-management-timeline-status-completed">
                        ✅ Completed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="loading-management-actions">
          <h4 className="loading-management-actions-title">
            📋 Supervisor Actions Available:
          </h4>
          <div className="loading-management-actions-description">
            {cart.status === "Pending" && (
              <p>
                <strong>Next Action:</strong> Click "START LOADING" button in
                cart card
              </p>
            )}
            {cart.status === "LoadingStarted" && (
              <p>
                <strong>Next Action:</strong> Click "Mark Crates Collected (TL)"
                when TL has collected all crates
              </p>
            )}
            {cart.status === "CratesCollected" && (
              <p>
                <strong>Next Action:</strong> Click "Mark TL Items Verification"
                when TL has verified all items
              </p>
            )}
            {cart.status === "TLItemsVerification" && (
              <p>
                <strong>Next Action:</strong> Click "Mark Cart Person
                Verification" when cart person has verified the items
              </p>
            )}
            {cart.status === "CartPersonVerification" && (
              <p>
                <strong>Next Action:</strong> Select a gate from the dropdown to
                assign the cart
              </p>
            )}
            {cart.status === "GateAssigned" && (
              <p>
                <strong>Next Action:</strong> Click "Mark Arrived at Gate (Cart
                Person)" when cart reaches assigned gate
              </p>
            )}
            {cart.status === "AtGate" && (
              <p>
                <strong>Next Action:</strong> Click "Mark Cart Can Leave" when
                everything is complete
              </p>
            )}
            {cart.status === "CartCanLeave" && (
              <p>
                <strong>Status:</strong> ✅ Cart is ready to leave - workflow complete! Orders with "Issued by Kitchen" status have been removed. You can now start a new loading cycle.
              </p>
            )}
          </div>

          <div className="loading-management-quick-actions">
            {cart.status === "LoadingStarted" && (
              <button
                onClick={() => updateCartState(cart.id, "CratesCollected")}
                className="loading-management-quick-action loading-management-quick-action-blue"
                disabled={isButtonLoading(cart.id, "CratesCollected")}
              >
                {isButtonLoading(cart.id, "CratesCollected") ? (
                  "⏳ Processing..."
                ) : (
                  <>
                    <Package className="loading-management-quick-action-icon" />
                    Mark Crates Collected
                  </>
                )}
              </button>
            )}
            {cart.status === "CratesCollected" && (
              <button
                onClick={() => updateCartState(cart.id, "TLItemsVerification")}
                className="loading-management-quick-action loading-management-quick-action-yellow"
                disabled={isButtonLoading(cart.id, "TLItemsVerification")}
              >
                {isButtonLoading(cart.id, "TLItemsVerification") ? (
                  "⏳ Processing..."
                ) : (
                  <>
                    <CheckCircle className="loading-management-quick-action-icon" />
                    Mark TL Items Verification
                  </>
                )}
              </button>
            )}
            {/* Show Cart Person Verification button in TLItemsVerification state */}
            {cart.status === "TLItemsVerification" && (
              <button
                onClick={() =>
                  updateCartState(cart.id, "CartPersonVerification")
                }
                className="loading-management-quick-action loading-management-quick-action-purple"
                disabled={isButtonLoading(cart.id, "CartPersonVerification")}
              >
                {isButtonLoading(cart.id, "CartPersonVerification") ? (
                  "⏳ Processing..."
                ) : (
                  <>
                    <CheckCircle className="loading-management-quick-action-icon" />
                    Mark Cart Person Verification
                  </>
                )}
              </button>
            )}
            {cart.status === "GateAssigned" && (
              <button
                onClick={() => updateCartState(cart.id, "AtGate")}
                className="loading-management-quick-action loading-management-quick-action-purple"
                disabled={isButtonLoading(cart.id, "AtGate")}
              >
                {isButtonLoading(cart.id, "AtGate") ? (
                  "⏳ Processing..."
                ) : (
                  <>
                    <MapPin className="loading-management-quick-action-icon" />
                    Mark Arrived at Gate
                  </>
                )}
              </button>
            )}
            {cart.status === "AtGate" && (
              <button
                onClick={() => {
                  const groupedOrderIds = getGroupedOrderIdsFromCart(cart);
                  updateCartState(
                    cart.id,
                    "CartCanLeave",
                    undefined,
                    groupedOrderIds,
                  );
                }}
                className="loading-management-quick-action loading-management-quick-action-green"
                disabled={isButtonLoading(cart.id, "CartCanLeave")}
              >
                {isButtonLoading(cart.id, "CartCanLeave") ? (
                  "⏳ Processing..."
                ) : (
                  <>
                    <Truck className="loading-management-quick-action-icon" />
                    Mark Cart Can Leave
                  </>
                )}
              </button>
            )}

            {/* Show Start New Loading Cycle button when in CartCanLeave state */}
            {cart.status === "CartCanLeave" && (
              <button
                onClick={() => {
                  const hasIndividualOrders =
                    filteredCartDetailData?.ordersForCart &&
                    filteredCartDetailData.ordersForCart.length > 0;

                  if (hasIndividualOrders) {
                    Swal.fire({
                      title: "Cannot Start Loading",
                      html: "This cart has <strong>individual orders</strong> that need to be grouped first.<br/><br/>Please create order groups before starting a new loading cycle.",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#3b82f6",
                      cancelButtonColor: "#6b7280",
                      confirmButtonText: "Go to Order Selection",
                      cancelButtonText: "Cancel",
                      reverseButtons: true,
                    }).then((result) => {
                      if (result.isConfirmed) {
                        setSupervisorTab("order-selection");
                      }
                    });
                  } else {
                    // Proceed with starting new loading cycle
                    updateCartState(cart.id, "LoadingStarted");
                  }
                }}
                className="loading-management-quick-action loading-management-quick-action-primary"
                disabled={isButtonLoading(cart.id, "LoadingStarted")}
              >
                {isButtonLoading(cart.id, "LoadingStarted") ? (
                  "⏳ Processing..."
                ) : (
                  <>
                    <Play className="loading-management-quick-action-icon" />
                    Start New Loading Cycle
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingManagement;